import { ArgDefType, ClientFuncArgBag, ClientFuncArgBagTagged, Filterable, RawArgDefOptions, RawArgDefOptions_List, RawArgDefOptions_Tree, RawArgDefOptions_TreeNode, RequestArg, Selectable } from "src/components/ReportBuilder/ReportBuilder.io"
import { assertIs, assertTruthy, copyViaJsonRoundTrip, exhaustiveCaseGuard, forceCheckedIndexedAccess, parseIntOrFail, requireNonNull, unreachable } from "src/helpers/utils"
import { Connective, NodeType } from "./UiQNode"
import { Integerlike } from "src/interfaces/InleagueApiV1"

export type ClientArgValue =
  & (
    // nulls on `value` here means "does not satisfy its `if` predicate"
    | {type: "single", value: null | string} // radio, text, date, number, select
    | {type: "multi", value: null | string[]} // checkbox, select-multi
    | {type: "tree", value: null | {[name: string]: string}} // select(tree)
  )
  & {
    satisfiesIf: boolean,
  }

export interface ClientSelectableFormData {
  selectable: Selectable,
  args: {
    [argName: string]: ClientArgValue
  },
  columnGroup: number,
  columnGroupPosition: number,
}

export interface ClientFilterableFormData {
  filterableDef: Filterable,
  /**
   * This is a slightly post-processed version of "all the selected values" having the shape expected by server provided JS snippets.
   * We could probably track this in real-time as part of ClientArgValue and then the types of ClientFuncArgBag and (collection of) ClientArgValue would be the same.
   * But at this time it is re-computed on change to a selected value.
   */
  clientFuncArgBagTagged: ClientFuncArgBagTagged,
  values: {
    /**
     * the name of the associated argument (see schema `argsDef`)
     */
    name: string,
    value: ClientArgValue
  }[]
}

export function FilterableFormData(
  filterableDef: Filterable,
  argValues: null | {[argName: string]: RequestArg},
  optionsMunger: (opts: RawArgDefOptions) => MungedArgDefOptions,
) : ClientFilterableFormData {
  switch (filterableDef.type) {
    case "functionlike": {
      const values = filterableDef.argsDef.map(def => {
        return {
          name: def.name,
          value: defaultValsForArgsDef(def, argValues, optionsMunger)
        }
      })

      // TODO: the 'if' exprs have all been set to true by default; and generally it will be the case that everything starts off as true,
      // but we need to handle the case where we have initially selected some default that then subsequently "ifs-away" other options.

      return {
        filterableDef,
        clientFuncArgBagTagged: createClientFuncArgBagTagged(filterableDef, values, optionsMunger),
        values,
      }
    }
    default: exhaustiveCaseGuard(
      // we used to have more than 1 type, now they're all 'functionlike', though we've retained the 'type' tag
      // just in case it would serve a future purpose.
      filterableDef.type
    )
  }
}

export function SelectableFormData(
  selectable: Selectable,
  argValues: null | {[argName: string]: RequestArg},
  optionsMunger: (opts: RawArgDefOptions) => MungedArgDefOptions,
  columnGroup: Integerlike,
  columnGroupPosition: Integerlike,
) : ClientSelectableFormData {
  return {
    selectable,
    args: (() => {
      const result : {[argname: string]: ClientArgValue} = {}
      if (selectable.argsDef) {
        for (const def of selectable.argsDef) {
          result[def.name] = defaultValsForArgsDef(def, argValues, optionsMunger)
        }
      }
      return result;
    })(),
    columnGroup: parseIntOrFail(columnGroup),
    columnGroupPosition: parseIntOrFail(columnGroupPosition),
  }
}

export function defaultValsForArgsDef(
  def: {name: string, type: ArgDefType, options?: RawArgDefOptions},
  argValues: null | {[argName: string]: RequestArg},
  optionsMunger: (opts: RawArgDefOptions) => MungedArgDefOptions,
) : ClientArgValue {
  const preexistingArgValue = argValues?.[def.name]
  const hadSomePreExistingValue = preexistingArgValue !== undefined && preexistingArgValue !== null

  switch (def.type) {
    case "date":
      // fallthrough
    case "number":
      // fallthrough
    case "text": {
      return {
        type: "single",
        value: typeof preexistingArgValue === "string" && preexistingArgValue
          ? preexistingArgValue
          : "",
        satisfiesIf: true
      }
    }
    case "radio": {
      const options = optionsMunger(requireNonNull(def.options))
      assertIs(options.type, "list")
      if (hadSomePreExistingValue && options.options.find(v => v.value /*weakEq*/ == preexistingArgValue)) {
        return {type: "single", value: preexistingArgValue.toString(), satisfiesIf: true}
      }
      else {
        return {
          type: "single",
          value: hadSomePreExistingValue
            ? "" // no value, to indicate to user that existing value (typically from a saved query) is no longer an option
            : forceCheckedIndexedAccess(options.options, 0)?.value || "",
          satisfiesIf: true
        }
      }
    }
    case "checkbox": {
      const options = optionsMunger(requireNonNull(def.options))
      assertIs(options.type, "list")

      const value = Array.isArray(preexistingArgValue)
        && preexistingArgValue.every(v => options.options.find(opt => opt.value /*weakEq*/ == v))
        ? preexistingArgValue
        : []

      return {
        type: "multi",
        value: value,
        satisfiesIf: true
      }
    }
    case "select": {
      const options = optionsMunger(requireNonNull(def.options))
      assertIs(options.type, "list")
      if (hadSomePreExistingValue && options.options.find(v => v.value /*weakEq*/ == preexistingArgValue)) {
        return {type: "single", value: preexistingArgValue.toString(), satisfiesIf: true}
      }
      else {
        return {
          type: "single",
          value: hadSomePreExistingValue
            ? "" // no value, to indicate to user that existing value (typically from a saved query) is no longer an option
            : forceCheckedIndexedAccess(options.options, 0)?.value || "",
          satisfiesIf: true
        }
      }
    }
    case "menu-tree": {
      const options = optionsMunger(requireNonNull(def.options))
      assertIs(options.type, "tree")
      return {
        type: "tree",
        value: isTreelikeValue(preexistingArgValue)
          ? preexistingArgValue // TODO: validate that they are valid
          : defaultTreeOptValues(options),
        satisfiesIf: true,
      }
    }
    case "select-many": {
      const options = optionsMunger(requireNonNull(def.options))
      if (options.type === "list") {
        const options = optionsMunger(requireNonNull(def.options))
        assertIs(options.type, "list")

        const value = Array.isArray(preexistingArgValue)
          && preexistingArgValue.every(v => options.options.find(opt => opt.value /*weakEq*/ == v))
          ? preexistingArgValue
          : []

        return {
          type: "multi",
          value: value,
          satisfiesIf: true,
        }
      }
      else if (options.type === "tree") {
        unreachable("this possiblity was pruned, we should delete this")
        // value = {
        //   type: "tree",
        //   value: isTreelikeValue(preexistingArgValue)
        //     ? preexistingArgValue
        //     : defaultTreeOptValues(options)
        // }
      }
      else {
        exhaustiveCaseGuard(options)
      }
    }
    default: exhaustiveCaseGuard(def.type)
  }

  function isTreelikeValue(v: any) : v is {[argName: string]: string} {
    return !!v && typeof v === "object" // yikes not much verifying we can do here
  }

  function defaultTreeOptValues(options: MungedArgDefOptions_Tree) {
    const result : {[nodeName: string]: string} = {}

    let workingChildList : MungedArgDefOptions_TreeNode[] | undefined = options.children

    for (let i = 0; i < options.levels.length; i++) {
      const name = options.levels[i].name
      const node : MungedArgDefOptions_TreeNode | undefined = requireNonNull(forceCheckedIndexedAccess(workingChildList ?? [], 0));

      result[name] = node.data.value

      workingChildList = node.children
    }

    return result
  }
}

export function fixupAllRedundantNodes(root: Connective) : Connective {
  const workedNodeIDs = new Set<number>();

  return worker(root);

  function worker(root: Connective) : Connective {
    for (let i = 0; i < root.children.length; ++i) {
      const child = root.children[i];

      if (workedNodeIDs.has(child.nodeID)) {
        continue;
      }

      switch (child.type) {
        case NodeType.connective:
          // This can mutate the children list we are iterating over.
          // We go depth first, and on the next iteration, the "current" node (root.children[i])
          // will be either:
          //  - unchanged
          //    - meaning iteration proceeds normally
          //  - replaced with 1-or-more nodes that have been lifted up
          //    - root.children[i]     points to the first lifted-up node (which has already been processed)
          //    - root.children[i+1]   points to the next lifted-up node (which has already been processed)
          //    - root.children[i+n]   points to the Nth lifted-up node (which has already been processed)
          //    - root.children[i+n+1] points to conceptually the "next" node ("next" being the i+1 NOW, had no merging taken place)
          //    - we don't "double work" nodes because we will recognize the i+1...i+n in the above examples by checking the
          //      `worked` Set for already worked nodeIDs
          worker(child);
          break;
        case NodeType.predicate:
          break;
        default: exhaustiveCaseGuard(child);
      }

      workedNodeIDs.add(child.nodeID)
    }

    root.mergeWithParentIfRedundant();

    return root;
  }
}

/**
 * return true if there is any node in the tree considered redundant
 */
export function containsAnyRedundantDescendantNodes(root: Connective) : boolean {
  for (const node of root.children) {
    switch (node.type) {
      case NodeType.connective: {
        if (node.isRedundant() || containsAnyRedundantDescendantNodes(node)) {
          return true;
        }
        continue;
      }
      case NodeType.predicate:
        continue;
      default: exhaustiveCaseGuard(node)
    }
  }
  return false;
}

export type MungedArgDefOptions_List = {type: "list", options: {label: string, value: string}[]}
export type MungedArgDefOptions_Tree = RawArgDefOptions_Tree & {__info_isAlias__?: never} // alias
export type MungedArgDefOptions_TreeNode = RawArgDefOptions_TreeNode & {__info_isAlias__?: never} // alias

export type MungedArgDefOptions =
  | MungedArgDefOptions_List
  | MungedArgDefOptions_Tree

export function mungeOptions(options: RawArgDefOptions, sharedOptionLookup: (name: string) => RawArgDefOptions_List | RawArgDefOptions_Tree) : MungedArgDefOptions {
  if (Array.isArray(options)) {
    return {type: "list", options}
  }
  else if (options.type === "shared") {
    const v = sharedOptionLookup(options.name)
    return mungeOptions(v, sharedOptionLookup)
  }
  else if (options.type === "tree") {
    return options
  }
  else {
    exhaustiveCaseGuard(options)
  }
}

/**
 * formkit loves to mutate your options list, generally when they the options list does not contain strings, and it's just terrible.
 * This is probably one of the most egregious behaviors of FormKit skewing its value proposition to net-negative.
 *
 * This is totally unreasonable behavior on their part because by default vue forms work with non-string values just fine.
 * Anyway, if your options list has non-string values (yes, including numbers), then it will re-write your options list.
 * It will do this IN PLACE. It will MUTATE YOUR OPTIONS LIST, and this means that your list that is somewhere in a data structure
 * elsewhere, to be reused or searched through or whatever, because it is YOUR options list, no longer has the data that you said it has.
 * They rewrite it to something like {label: <<original label>>, value: "__maskN"} where "__maskN" is a value that makes sense to them to
 * map back the __maskN value to the original value. But that's nonsense, it's not their options list. They just destroyed our ability
 * to use our own options list as we intended. If we use v-model we have a high chance of writing garbage "__maskN" values into our v-modeled
 * data.
 *
 * Make sure to do this at reasonable choke points, otherwise it starts writing garbage into lists and values turn
 * into strings that look like "__mask1".
 */
export const fkbs_defensiveCopy = copyViaJsonRoundTrip

export function createClientFuncArgBagTagged(
  filterable: Filterable,
  userValues: {name: string, value: ClientArgValue}[],
  optionsMunger: (opts: RawArgDefOptions) => MungedArgDefOptions,
) : ClientFuncArgBagTagged {
  if (filterable.argsDef.length !== userValues.length) {
    throw Error("out of sync filterDef and values")
  }

  const result : ClientFuncArgBagTagged = {};

  for (let i = 0; i < userValues.length; i++) {
    const argDef = filterable.argsDef[i];
    const userValue = userValues[i]

    assertTruthy(userValue.name === argDef.name);

    switch (argDef.type) {
      case "text":
        // fallthrough
      case "date":
        // fallthrough
      case "number": {
        assertIs(userValue.value.type, "single")
        result[argDef.name] = userValue.value.value === null
          ? null
          : {
            type: "single",
            value: {
              label: "",
              value: userValue.value.value
            }
          }
        break;
      }
      case "radio":
        // fallthrough
      case "checkbox": {
        const opts = optionsMunger(requireNonNull(argDef.options))
        assertIs(opts.type, "list")
        doListOpts(opts)
        break;
      }
      case "select": {
        const opts = optionsMunger(requireNonNull(argDef.options))

        if (opts.type === "list") {
          assertIs(userValue.value.type, "single")
          result[argDef.name] = userValue.value.value === null
            ? null
            : {
              type: "single",
              value: {
                label: opts.options.find(v => v.value == userValue.value.value)?.label ?? "",
                value: userValue.value.value
              }
            }
        }
        else {
          unreachable("select should have 'list' type value")
        }

        break;
      }
      case "menu-tree": {
        const opts = optionsMunger(requireNonNull(argDef.options))
        assertIs(opts.type, "tree")
        result[argDef.name] = (() => {
          if (userValue.value.value === null) {
            return null
          }

          assertIs(userValue.value.type, "tree")
          // result, initialized with dummy defaults, we need to descend the tree to find the real label/value pairs
          const result = (() => {
            const r : {[nodeName: string]: {nodeLabel: string, label: string, value: string}} = {}
            for (const level of opts.levels) {
              r[level.name] = {nodeLabel: level.label, label: "", value: ""}
            }
            return r;
          })()

          // descend tree, finding label/value pairs for the selected things as we go

          let levelIdx = 0
          let workingList : MungedArgDefOptions_TreeNode[] | undefined = opts.children;

          while (levelIdx < opts.levels.length) {
            const val = userValue.value.value[opts.levels[levelIdx].name]
            const node : MungedArgDefOptions_TreeNode | undefined = workingList?.find(v => v.data.value /*weakEq*/ == val)

            if (!node) {
              // this should be an incomplete selection, like the first N/2 levels are selected but not the last N/2
              // we assume we initialized the result with defaults, so the "incomplete" levels are at least initialized with dummy defaults,
              // they just won't have meaningful values
              break;
            }

            result[opts.levels[levelIdx].name] = {
              nodeLabel: result[opts.levels[levelIdx].name].nodeLabel, // retain from earlier default initialization
              label: node.data.label,
              value: node.data.value
            }
            levelIdx += 1
            workingList = node.children
          }

          return {type: "tree", value: result}
        })()
        break;
      }
      case "select-many": {
        const opts = optionsMunger(requireNonNull(argDef.options))
        assertIs(opts.type, "list")
        doListOpts(opts)
        break;
      }
      default: exhaustiveCaseGuard(argDef.type)
    }

    function doListOpts(opts: MungedArgDefOptions_List) {
      assertIs(userValue.value.type, "multi")
      result[argDef.name] = userValue.value.value === null
        ? null
        : {type: "multi", value: userValue.value.value.map(userValue => {
          const label = opts.options.find(v => v.value /*weakEq*/ == userValue)?.label ?? "???"
          return {
            label: label,
            value: userValue,
          }
        })};
    }
  }

  return result;
}
